/**
 * The proposal a visit block was written for.
 *
 * The block's payload carries only the proposal's id. Its status dates the card, and its
 * own window is what a rescheduled card is redrawn from ({@link resolveVisitBlockView}). Both order threads read it the same way, so
 * the read config lives here once rather than as a per-surface hook — the office copy
 * pinned `staleTime: Infinity, gcTime: Infinity`, which meant a proposal cancelled by
 * someone else never refetched for the life of the page.
 *
 * Default cache policy, matching {@link useChannelTimePreference}: a re-propose reaches
 * this query through `visit.changed`, whose refresh covers every time proposal (or
 * `useVisitScenarioInvalidate` for a write that cannot name its visit). Both directions
 * run it — the sender on its own write, and the RECEIVER on the event the platform
 * announces on the visit's order, which `ChannelView` listens on wherever the thread is
 * shown. Until the receiving half existed, an inbox
 * thread showed the new message against a card still holding the old status.
 *
 * Returns `undefined` for a block that names no proposal and while the row is in flight
 * — what the decision reads as "cannot date this card".
 */

import { useGetTimeProposalsByTimeProposalId } from "@birdwatch/client";
import type { VisitBlockProposal } from "../_libs/visit-proposal-history";

export function useVisitProposal(
  timeProposalId?: string,
): VisitBlockProposal | undefined {
  const { data } = useGetTimeProposalsByTimeProposalId({
    timeProposalId: timeProposalId ?? "",
  }).get({ enabled: !!timeProposalId });

  return data;
}
